import React, { useState, useEffect, useRef } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PaperCard } from 'components/Card';
import { paths } from 'routes';
import { useAuth } from 'lib/auth';
import { RedMessageBar } from 'components/RedMessageBar';
import { FormProvider, TextField } from 'components/Form';
import { useForm } from 'react-hook-form';
import { auth } from "lib/firebase"
import { signInWithEmailAndPassword } from 'firebase/auth';
import { ConfirmationSentForm } from './ConfirmationSentForm';

export const VerifyEmailForm = ({
  onSuccess,
  actionCode,
  apiKey,
}) => {
  const { t } = useTranslation();
  const { verifyActionCode, sendverificationemail, firebaseUser, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ error, setError ] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [values, setValues] = useState(null);
  const initialLoad = useRef(true);

  useEffect(() => {
    const verifyCode = async () => {
      try {
        await verifyActionCode(actionCode);
      } catch (error) {
        setError(error.message);
      } finally {
        logout();
        setLoading(false);
      }
    }

    if (initialLoad.current) {
      initialLoad.current = false;
      verifyCode();
    }
  }, []);

  const resendVerificationEmail = async () => {
    setLoading(true);
    setError('');
    try {
      if (!firebaseUser) {
        await signInWithEmailAndPassword(auth, email, password);
      }

      setValues({ companyEmail: email || firebaseUser?.email });
      await sendverificationemail();
      setEmailSent(true);
    } catch (error) {
      setError(error.message);
    } finally {
      logout();
      setLoading(false);
    }
  }

  const methods = useForm();

  return (
    <>
    {!loading && (
      <>
      {emailSent ? (
        <ConfirmationSentForm
          values={ values }
          initLoad={false}
          standalone
        />
      ) :
        <PaperCard px={{ xs: 2, sm: 4 }} width={{ xs: 1, sm: 368 }} maxWidth={368} sx={{ my: 5 }}>
          <Stack>
            {error && (
              <>
                <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
                  {t('website:auth.verifyEmail.heading')}
                </Typography>
                <RedMessageBar message={error} sx={{mb:2}} />
                {!firebaseUser && (
                  <FormProvider methods={methods} onSubmit={(e) => e.preventDefault()}>
                    <Stack>
                      <TextField
                        name="companyEmail"
                        label={t('website:auth.verifyEmail.emailLabel')}
                        placeholder={t('website:auth.verifyEmail.emailPlaceholder')}
                        required={true}
                        type="email"
                        autoComplete="username"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        fullWidth
                      />
                      <TextField
                        name="password"
                        label={t('website:auth.verifyEmail.passwordLabel')}
                        placeholder={t('website:auth.verifyEmail.passwordPlaceholder')}
                        required={true}
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={Boolean(methods?.formState?.errors?.password)}
                        helperText={methods?.formState?.errors?.password?.message}
                        sx={{ mb: 0.5 }}
                        fullWidth
                      />
                      <Stack direction={"row"} width={1} justifyContent={"space-between"}>
                        <Button size="medium" variant="contained" color="primary" sx={{ mt: 2, mb: 0.5 }} onClick={resendVerificationEmail}>
                          {t('website:auth.verifyEmail.loginAndResendButton')}
                        </Button>
                        <Button size="medium" variant="contained" color="secondary" sx={{ mt: 2, mb: 0.5 }} href={paths.auth.login}>
                          {t('website:auth.verifyEmail.returnToHomeButton')}
                        </Button>
                      </Stack>
                    </Stack>
                  </FormProvider>
                )}
              </>
            )}
            {!error && (
              <>
                <Typography variant="h5" sx={{ mb: 3 }}>
                  {t('website:auth.verifyEmail.successHeading')}
                </Typography>
                <Stack>
                  <Typography variant="body1">
                    {t('website:auth.verifyEmail.successBody')}
                  </Typography>

                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    sx={{
                      mt: 2,
                      mb: 0.5,
                      bgcolor: "primary.light",
                      '&:hover': {
                        backgroundColor: "primary.light",
                      },
                    }}
                    href={paths.homepage}
                  >
                    {t('website:auth.verifyEmail.proceedToLoginButton')}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </PaperCard>
      }
      </>
    )}
    </>
  );
};
